import React, { useCallback, useEffect, useRef } from 'react';

import { TicketSelectionLayout, TitleBox } from '@tsinternetsale/common';
import { SelectedTicketsManagement } from './selected-tickets-management';
import { TicketSelectionVenuePlan } from './ticket-selection-venue-plan';
import { TicketSelectionSearchPanel } from './ticket-selection-search-panel';
import { PleaseWaitSpinner } from '../../../../jsxctl/utilctl';
import "./event-date-ticket-selection-unrestricted.scss";

import { Point, SelectedTicketsOptions } from '../../../logic/models';

import TranslatorManager from 'ts-translate-js';
import { useBrowserInfo, useIFrameResize } from '../../../logic/hooks/utils';
import { useVenuePlanControl } from '../../../logic/hooks/use-venue-plan-control';
import { Vector2 } from 'peet-math';
import { SearchModeEnum, useAddTicketsToCartStore } from '../../../stores';
import { SmallTicketSearch } from '../selecttickets/small-ticket-search';
import { useAddPackageToCartStoreExt } from '../../../stores/add-to-cart-stores/add-package-to-cart-store-ext';

const tr = TranslatorManager.boundTR;

type EventDateTicketSelectionUnrestrictedProps = {
    options: SelectedTicketsOptions;
}

// INFO: New sale

export function EventDateTicketSelectionUnrestricted({options}: EventDateTicketSelectionUnrestrictedProps) {
    const [ eventDateId, selectedTicketsByEventDateId_EventDate ] = useAddTicketsToCartStore(s => [ s.eventDateId, s.selectedTicketsByEventDateId ]);
    const [ eventDate, fetchingEventDate ] = useAddTicketsToCartStore(s => [ s.eventDate, s.fetchingEventDate ]);
    

    const [ preferredSelectedSectionId, forceSectionInPromo ] = useAddTicketsToCartStore(s => [ s.preferredSelectedSectionId, s.eventDate?.ForceSectionInPromo ]);

    const [ isAPackageSale, packageId, curPackage ] = useAddPackageToCartStoreExt(s => [ s.isAPackageSale, s.packageId, s.package ]);
    
    const [ packageSelectedEventDateIds, selectedTicketsByEventDateId_Package ] = useAddPackageToCartStoreExt(s => [ s.selectedEventDateIds, s.selectedTicketsByEventDateId]);
    
    const [ removeAllPackageTickets, hasSelectedPackageItems ] = useAddPackageToCartStoreExt(s => [ s.removeAllTickets, s.hasSelectedItems ]);
    const [ removeAllTickets, hasSelectedItems ] = useAddTicketsToCartStore(s => [ s.removeAllTickets, s.hasSelectedItems ]);
    

    const [ startTicketSearchByPointOnPlan, startTicketSearchBySection ] = useAddTicketsToCartStore(s => [ s.startTicketSearchByPointOnPlan, s.startTicketSearchBySection ]);
    
    const [ searchTicketMode, setSearchTicketMode ] = useAddTicketsToCartStore(s => [ s.searchTicketMode, s.setSearchTicketMode ]); // TODO: Dépendra de si l'événement a un plan de salle
    
    const showBestTicketFirstOnInternet = eventDate?.ShowBestTicketFirstOnInternet || curPackage?.ShowBestTicketFirstOnInternet || preferredSelectedSectionId > 0 || forceSectionInPromo;
    const selectedTicketsByEventDateId = isAPackageSale() ? selectedTicketsByEventDateId_Package : selectedTicketsByEventDateId_EventDate;

    const startTicketRemovalProcess_EventDate = useAddTicketsToCartStore(s => s.startTicketRemovalProcessWithConfirm);
    const startTicketRemovalProcess_Package = useAddPackageToCartStoreExt(s => s.startItemRemovalByTicketIdsProcessWithConfirm);

    const startTicketRemovalProcess = isAPackageSale() ? startTicketRemovalProcess_Package : startTicketRemovalProcess_EventDate;

    const addNewTickets = useAddTicketsToCartStore(s => s.addNewTickets);
    const addNewPackages = useAddPackageToCartStoreExt(s => s.addNewTickets);
    
const loading = fetchingEventDate || searchTicketMode === SearchModeEnum.UNDECIDED;
    const planControl = useVenuePlanControl();
    const browserSize = useBrowserInfo();

    const selectedTickets = selectedTicketsByEventDateId[eventDateId]?.selectedTickets || [];

    // A little tool that keeps track of where to move once the plan is loaded
    const whereToMoveWhenLoaded = useRef({ moveToOverview: false, position: null, zoomAtLeast: null });

    useIFrameResize();

    

    useEffect(() => {
        if(eventDate && searchTicketMode === SearchModeEnum.UNDECIDED) {
            if(showBestTicketFirstOnInternet) {
                setSearchTicketMode(SearchModeEnum.ADVANCED_SEARCH);
                return;
            }

            // Set the default view
            if(eventDate.HasVenuePlan // We show the plan if there's plan!
                && eventDate.ShowAvailablePlacesOnInternet) { // But we don't do it by default if they don't show the available places because it's not super beautiful...
                setSearchTicketMode(SearchModeEnum.VENUE_PLAN);
            } else {
                setSearchTicketMode(SearchModeEnum.ADVANCED_SEARCH);
            }
        }
    }, [eventDate, forceSectionInPromo, preferredSelectedSectionId, searchTicketMode, setSearchTicketMode, showBestTicketFirstOnInternet]);

    const handleFindMeTickets = useCallback(() => {
        setSearchTicketMode(SearchModeEnum.ADVANCED_SEARCH);
    }, [setSearchTicketMode]);
    
    const handleBackToVenuePlan = useCallback(() => {
        setSearchTicketMode(SearchModeEnum.VENUE_PLAN);
    }, [setSearchTicketMode]);

    const nextFocusPoint = useRef<Point>(null);

    // Remote control
    const handleShowPlanRequest = useCallback((focusOn?: Point) => { 
        if(eventDate && eventDate.HasVenuePlan) { // Execute the request only if we have a plan
            nextFocusPoint.current = focusOn;
            setSearchTicketMode(SearchModeEnum.VENUE_PLAN);
        }
    }, [eventDate, setSearchTicketMode]);

    // Auto move to overview or seats when changing the view to the plan
    useEffect(() => {
        if(searchTicketMode === SearchModeEnum.VENUE_PLAN && planControl) { 
            if(nextFocusPoint.current) {
                const focusPoint = nextFocusPoint.current;
                const center = { x: focusPoint.X, y: focusPoint.Y };
                const zoom = 1;
                
                // If plan is not loaded, we'll move when it is
                whereToMoveWhenLoaded.current.position = center;
                whereToMoveWhenLoaded.current.zoomAtLeast = zoom;

                // If plan is loaded
                planControl.moveToXYZoomAtLeast(center, zoom, 1000);
                nextFocusPoint.current = null;
            } else {
                // If plan is not loaded, we'll move when it is
                whereToMoveWhenLoaded.current.moveToOverview = true;
                // If plan is loaded
                planControl.moveToOverview();
            }
        }
    }, [searchTicketMode, planControl]);
    
    const handlePlanLoaded = useCallback(() => {
        if(whereToMoveWhenLoaded.current.position)
            planControl.moveToXYZoomAtLeast(whereToMoveWhenLoaded.current.position, whereToMoveWhenLoaded.current.zoomAtLeast, 1000);
        else
            planControl.moveToOverview();
    }, [planControl]);

    useEffect(() => {
        if(planControl) {
            planControl.onShowPlanRequest(handleShowPlanRequest);
        }
        return () => {
            if(planControl) {
                planControl.offShowPlanRequest(handleShowPlanRequest);
            }
        };
    }, [planControl, handleShowPlanRequest]);
    
    const handleOnClearAllTickets = (e) => {
        e.stopPropagation(); 
        e.preventDefault();
        
        if(isAPackageSale())
            removeAllPackageTickets();
        else
            removeAllTickets();
    };    

    const handleOnFingerSearch = useCallback((_eventDateId: number, pointOnPlan: Vector2, _planEngine: any) => { startTicketSearchByPointOnPlan(pointOnPlan); }, [startTicketSearchByPointOnPlan]);

    const advancedSearchButton = <button className='btn btn-default btn-xs' onClick={handleFindMeTickets}>{tr("Recherche avancée")}</button>;

    const smallTicketSearchCtrl = <SmallTicketSearch
        maxQty={10}
        limitQtySales={eventDate.LimitQtySales}
        suggestedQtySales={eventDate.SuggestedQtySales}
        internetTicketQTYMultipleOf={eventDate.InternetTicketQTYMultipleOf}
    />;

    // Fill the left column
    let leftCol: React.ReactElement = null;
    const leftColTitle = tr("Choix de places");
    if(searchTicketMode === SearchModeEnum.UNDECIDED) {
        leftCol = <TitleBox title={leftColTitle}>
            <p>TODO: Loading... Wait spinner here</p>
        </TitleBox>;

    } else if(searchTicketMode === SearchModeEnum.VENUE_PLAN) {
        leftCol = <div>
            {(browserSize === "Mobile" || browserSize === "Tablet") && smallTicketSearchCtrl}
            <TitleBox title={leftColTitle} rightContent={advancedSearchButton}>
                <label className="venue-plan-infos">{tr("Cliquez sur le plan pour sélectionner les places de votre choix")}</label>
                <TicketSelectionVenuePlan 
                    eventDateId={eventDateId} 
                    packageId={isAPackageSale() ? packageId : null}
                    packageSelectedEventDateIds={isAPackageSale() ? packageSelectedEventDateIds : null}
                    selectedTickets={selectedTickets.map(ticket => ticket.ticket)}
                    onPlanLoaded={handlePlanLoaded}
                    onFingerSearch={handleOnFingerSearch}
                    onSectionClick={startTicketSearchBySection}
                    onSelectedTicketsAdded={ isAPackageSale() ? addNewPackages : addNewTickets}
                    onSelectedTicketsRemoved={startTicketRemovalProcess}
               />
            </TitleBox>
        </div>;
    } else {
        leftCol = <TitleBox title={leftColTitle}>
            <TicketSelectionSearchPanel 
                options={{ 
                    ...options, 
                    clearPreviousSelection: false,
                    onGoBackToSeatPlan: handleBackToVenuePlan
                }}
            />
        </TitleBox>;
    }

    const hasAddToCartItems = hasSelectedItems || hasSelectedPackageItems();

    const clearSelectionButton = hasAddToCartItems && 
        //TODO: Refactor this as a button
        // eslint-disable-next-line sonarjs/anchor-is-valid        
        <a href="#" onKeyDown={handleOnClearAllTickets} onClick={handleOnClearAllTickets}>{tr("vider")}</a>;

    const showRightCol = searchTicketMode !== SearchModeEnum.ADVANCED_SEARCH || selectedTickets.length > 0;
    const rightCol = showRightCol && <TitleBox title={tr("Votre sélection")} rightContent={clearSelectionButton}>
        {searchTicketMode !== SearchModeEnum.ADVANCED_SEARCH && browserSize === "Desktop" && smallTicketSearchCtrl}
        <SelectedTicketsManagement />
    </TitleBox>;

    return loading 
        ? <PleaseWaitSpinner/>
        : <TicketSelectionLayout
            className="event-date-ticket-selection-unrestricted"
            leftCol={leftCol}
            rightCol={rightCol} 
        />;
}
